import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
    Container, 
    Paper, 
    TextField, 
    Button, 
    Typography, 
    Box, 
    Alert, 
    CircularProgress,
    Grid,
    Link,
    useTheme,
    alpha,
    FormHelperText
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DEFAULT_THEME, type ThemeColors } from '../constants/themeDefaults';
import LoginLayout from '../components/layout/LoginLayout';

// Add this interface
interface RegisterFormValues {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
}

// Define validation interface to match RegisterDto.cs
interface ValidationState {
    email: string | null;
    password: string | null;
    firstName: string | null;
    lastName: string | null;
}

// Define interface for API responses
interface RegisterResponse {
    succeeded: boolean;
    token: string | null;
    refreshToken: string | null;
    expiresAt: string | null;
    tokenType: string;
    errors: string[] | null;
    user: any | null;
}

export const Register: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [step, setStep] = useState(1);
    const { register } = useAuth();
    const theme = DEFAULT_THEME;
    const navigate = useNavigate();
    const { priceId } = useParams<{ priceId: string }>();
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    
    // Add validation state
    const [validationErrors, setValidationErrors] = useState<ValidationState>({
        email: null,
        password: null,
        firstName: null,
        lastName: null
    });
    
    // Add form submission tracking
    const [formSubmitted, setFormSubmitted] = useState(false);
    
    // Update to store array of error messages from API
    const [apiErrors, setApiErrors] = useState<string[]>([]);
    
    // Check if form is valid
    const isFormValid = 
        !validationErrors.email && 
        !validationErrors.password && 
        !validationErrors.firstName && 
        !validationErrors.lastName &&
        email.trim() !== '' &&
        password.trim() !== '' &&
        firstName.trim() !== '' &&
        lastName.trim() !== '';

    // Note: priceId is optional - registration can work without it
    // If priceId is provided, it will be used for payment processing after registration

    // Validate email
    const validateEmail = (value: string) => {
        if (!value.trim()) {
            return "Email address is required";
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return "Please enter a valid email address";
        }
        
        return null;
    };
    
    // Validate password
    const validatePassword = (value: string) => {
        if (!value.trim()) {
            return "Password is required";
        }
        
        if (value.length < 8 || value.length > 100) {
            return "Password must be between 8 and 100 characters";
        }
        
        return null;
    };
    
    // Validate first name
    const validateFirstName = (value: string) => {
        if (!value.trim()) {
            return "First name is required";
        }
        
        if (value.length > 50) {
            return "First name cannot be longer than 50 characters";
        }
        
        return null;
    };
    
    // Validate last name
    const validateLastName = (value: string) => {
        if (!value.trim()) {
            return "Last name is required";
        }
        
        if (value.length > 50) {
            return "Last name cannot be longer than 50 characters";
        }
        
        return null;
    };
    
    // Add handlers for blur events
    const handleBlur = (field: keyof ValidationState) => {
        let errorMessage: string | null = null;
        
        switch(field) {
            case 'email':
                errorMessage = validateEmail(email);
                break;
            case 'password':
                errorMessage = validatePassword(password);
                break;
            case 'firstName':
                errorMessage = validateFirstName(firstName);
                break;
            case 'lastName':
                errorMessage = validateLastName(lastName);
                break;
        }
        
        setValidationErrors(prev => ({
            ...prev,
            [field]: errorMessage
        }));
    };
    
    // Validate all fields
    const validateForm = () => {
        const newValidationState: ValidationState = {
            email: validateEmail(email),
            password: validatePassword(password),
            firstName: validateFirstName(firstName),
            lastName: validateLastName(lastName)
        };
        
        setValidationErrors(newValidationState);
        
        // Return true if no errors
        return !Object.values(newValidationState).some(error => error !== null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setApiErrors([]);
        setFormSubmitted(true);
        
        // Validate all fields before submission
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);

        try {
            // Register the user
            const result = await register(email, password, firstName, lastName) as RegisterResponse;
            
            console.log('Registration response:', result);
            
            if (result.succeeded && result.token) {
                // Registration successful
                localStorage.setItem('token', result.token);
                localStorage.setItem('user', JSON.stringify(result.user));
                
                // Immediately redirect to payment page or dashboard
                if (priceId) {
                    navigate(`/payment-details/${priceId}`);
                } else {
                    navigate('/dashboard');
                }
            } else {
                // Check for API validation errors
                if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
                    // Set API errors for display
                    setApiErrors(result.errors);
                } else {
                    // Generic error if no specific errors provided
                    setError('Registration failed. Please check your information and try again.');
                }
            }
        } catch (err: any) {
            console.error('Registration exception:', err);
            
            // Try to extract errors from the error response
            if (err.response && err.response.data) {
                const responseData = err.response.data;
                
                if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                    setApiErrors(responseData.errors);
                    return;
                }
            }
            
            // Default error message if we can't extract specific errors
            setError(err.message || 'An error occurred during registration');
        } finally {
            setLoading(false);
        }
    };

    return (
        <LoginLayout>
            <Box 
                sx={{ 
                    minHeight: '100vh',
                    display: 'flex',
                    bgcolor: theme.backgroundColor
                }}
            >
                <Grid container>
                    {/* Left side - Form */}
                    <Grid item xs={12} md={5} lg={4} component={Paper} elevation={6} square
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: { xs: 4, md: 8 },
                            zIndex: 1,
                            position: 'relative',
                            boxShadow: '0 0 40px rgba(0,0,0,0.1)',
                            backgroundColor: theme.backgroundColor,
                        }}
                    >
                        <Box sx={{ mb: 5, display: 'flex', alignItems: 'center' }}>
                            <CalendarMonthIcon sx={{ color: theme.navigationColor, mr: 1, fontSize: '2rem' }} />
                            <Typography variant="h4" fontWeight="700" color={theme.textColor}>
                                MindShare
                            </Typography>
                        </Box>

                        <Typography variant="h4" fontWeight="700" gutterBottom color={theme.textColor}>
                            Create an account
                        </Typography>

                        <Typography variant="body1" color="text.secondary" mb={4}>
                            Start your 7-day free trial, no credit card required
                        </Typography>

                        {/* Display validation errors from the frontend */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Display API validation errors */}
                        {apiErrors.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Alert severity="error" sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Please fix the following issues:
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                                        {apiErrors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </Alert>
                            </Box>
                        )}

                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        autoComplete="given-name"
                                        name="firstName"
                                        required
                                        fullWidth
                                        id="firstName"
                                        label="First Name"
                                        autoFocus
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        onBlur={() => handleBlur('firstName')}
                                        error={formSubmitted ? !!validationErrors.firstName : false}
                                        helperText={formSubmitted ? validationErrors.firstName : null}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.navigationColor,
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="lastName"
                                        label="Last Name"
                                        name="lastName"
                                        autoComplete="family-name"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        onBlur={() => handleBlur('lastName')}
                                        error={formSubmitted ? !!validationErrors.lastName : false}
                                        helperText={formSubmitted ? validationErrors.lastName : null}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.navigationColor,
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label="Email Address"
                                name="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={() => handleBlur('email')}
                                error={formSubmitted ? !!validationErrors.email : false}
                                helperText={formSubmitted ? validationErrors.email : null}
                                sx={{
                                    mb: 2,
                                    mt: 3,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.navigationColor,
                                        },
                                    },
                                }}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type="password"
                                id="password"
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onBlur={() => handleBlur('password')}
                                error={formSubmitted ? !!validationErrors.password : false}
                                helperText={formSubmitted ? validationErrors.password : null}
                                sx={{
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.navigationColor,
                                        },
                                    },
                                }}
                            />
                            
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{
                                    py: 1.5,
                                    mt: 1,
                                    mb: 2,
                                    backgroundColor: theme.navigationColor,
                                    color: theme.secondaryColor,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.navigationColor, 0.9),
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: alpha(theme.navigationColor, 0.5),
                                        color: alpha(theme.secondaryColor, 0.7)
                                    }
                                }}
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign Up'}
                            </Button>
                            
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Already have an account?{' '}
                                    <Link 
                                        component={RouterLink} 
                                        to="/login" 
                                        sx={{ 
                                            fontWeight: 600,
                                            color: theme.navigationColor,
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        Sign In
                                    </Link>
                                </Typography>
                            </Box>
                            
                            <Typography variant="caption" color="text.secondary" display="block" align="center" mt={3}>
                                By signing up, you agree to our{' '}
                                <Link component={RouterLink} to="/terms" color={theme.navigationColor}>
                                    Terms of Service
                                </Link>{' '}
                                and{' '}
                                <Link component={RouterLink} to="/privacy" color={theme.navigationColor}>
                                    Privacy Policy
                                </Link>
                            </Typography>

                            <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                    position: 'absolute',
                                    bottom: 8,
                                    left: 8,
                                    fontSize: '0.7rem',
                                    opacity: 0.7,
                                    '&:hover': { 
                                        opacity: 1 
                                    }
                                }}
                            >
                                <Link 
                                    component={RouterLink} 
                                    to="/credits" 
                                    sx={{ 
                                        color: theme.navigationColor,
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    Image Credits
                                </Link>
                            </Typography>
                        </Box>
                    </Grid>
                    
                    {/* Right side - Illustration */}
                    <Grid item xs={false} md={7} lg={8} 
                        sx={{
                            backgroundImage: 'url(/images/register-illustration.jpg)',
                            backgroundRepeat: 'no-repeat',
                            backgroundColor: theme.subtleAccent,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            display: { xs: 'none', md: 'block' },
                            position: 'relative',
                            minHeight: '100vh',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: alpha(theme.navigationColor, 0.1),
                                zIndex: 0,
                            }
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: { xs: 40, md: 80 },  // Increased bottom margin
                                left: 40,
                                zIndex: 1,
                                maxWidth: 400,
                                color: theme.backgroundColor,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                padding: 3,
                                borderRadius: 2,
                                transform: 'translateY(-40px)',  // Move up by 40px
                                '& h3, & h6': {
                                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                }
                            }}
                        >
                            <Typography variant="h3" fontWeight="700" gutterBottom>
                                Your appointments, your way
                            </Typography>
                            <Typography variant="h6">
                                Work online or offline with MindShare's powerful scheduling platform
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </LoginLayout>
    );
}; 